import { Injectable } from '@angular/core';
import { PhoneNumberPipe } from '../pipes/phone-number.pipe';
import { TitleCasePipe, UpperCasePipe } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import * as Parse from 'parse';

export interface ICustomerInfo {
    name: string;
    email: string;
    street: string;
    city: string;
    zip: string;
    state: string;
    phone: string;
    notes: string;
    deliverSMS: boolean;
    deliverEmail: boolean;
    preferredAgent?: string;
}

export interface IPurchaseCriteria {
    priceMin: string;
    priceMax: string;
    roiMin: string;
    returnMin: string;
    wantsRental: boolean;
    wantsFlip: boolean;
    wantsLand: boolean;
}

@Injectable()
export class CustomerStateService {
    public loading: boolean = false;
    public saving: boolean = false;
    public lastSaved: Date | undefined;
    public deleting: boolean = false;
    public updating: boolean = false;
    public reload: boolean = false;
    public newCustomer: boolean = false;
    public publicSignup: boolean = false;
    public hasUserSlug: boolean = false;
    public tokenAccess: boolean = false;
    public inPublicMode: boolean = false;
    public customer: Parse.Object = new Parse.Object('Customer');
    public isAdmin: boolean = false;
    public agentNames: Parse.Object[] = [];

    public customerInfo: ICustomerInfo = {
        name: '',
        email: '',
        street: '',
        city: '',
        zip: '',
        state: '',
        phone: '',
        notes: '',
        deliverSMS: true,
        deliverEmail: true
    };

    public purchaseCriteria: IPurchaseCriteria = {
        priceMin: '',
        priceMax: '',
        roiMin: '',
        returnMin: '',
        wantsRental: false,
        wantsFlip: false,
        wantsLand: false
    };

    public isCustomerFormValid: boolean = false;
    public customerFormValidity: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public autoSaveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private phonePipe: PhoneNumberPipe,
        private titleCasePipe: TitleCasePipe,
        private upperCasePipe: UpperCasePipe
    ) {
        this.customerFormValidity.subscribe((isValid: boolean) => this.isCustomerFormValid = isValid);
    }

    hydrateCustomerInfo() {
        this.customerInfo = {
            name: this.titleCasePipe.transform(this.customer.get('name') || ''),
            email: this.customer.get('email') || '',
            street: this.titleCasePipe.transform(this.customer.get('street') || ''),
            city: this.titleCasePipe.transform(this.customer.get('city') || ''),
            zip: this.customer.get('zip') || '',
            state: this.upperCasePipe.transform(this.customer.get('state') || ''),
            phone: this.phonePipe.transform(this.customer.get('phone') || ''),
            notes: this.customer.get('notes') || '',
            deliverSMS: !!this.customer.get('deliverSMS'),
            deliverEmail: !!this.customer.get('deliverEmail')
        };

        if (!this.newCustomer) {
            this.customerInfo = {
                ...this.customerInfo,
                preferredAgent: this.customer.get('preferredAgent').id
            };
        }
    }

    hydratePurchaseCriteria() {
        this.purchaseCriteria = {
            priceMin: String(this.customer.get('priceMin') || ''),
            priceMax: String(this.customer.get('priceMax') || ''),
            roiMin: String(this.customer.get('roiMin') * 100 || ''),
            returnMin: String(this.customer.get('returnMin') || ''),
            wantsRental: this.customer.get('wantsRental') || false,
            wantsFlip: this.customer.get('wantsFlip') || false,
            wantsLand: this.customer.get('wantsLand') || false
        };
    }

    resetState() {
        this.resetCustomerInfo();
        this.resetPurchaseCriteria();

        this.loading = false;
        this.saving = false;
        this.lastSaved = undefined;
        this.deleting = false;
        this.reload = false;
        this.newCustomer = false;
        this.publicSignup = false;
        this.hasUserSlug = false;
        this.tokenAccess = false;
        this.inPublicMode = false;
        this.customer = new Parse.Object('Customer');
        this.isAdmin = false;
        this.agentNames = [];
    }

    resetCustomerInfo() {
        this.customerInfo = {
            name: '',
            email: '',
            street: '',
            city: '',
            zip: '',
            state: '',
            phone: '',
            notes: '',
            deliverSMS: true,
            deliverEmail: true,
            preferredAgent: ''
        };
    }

    resetPurchaseCriteria() {
        this.purchaseCriteria = {
            priceMin: '',
            priceMax: '',
            roiMin: '',
            returnMin: '',
            wantsRental: false,
            wantsFlip: false,
            wantsLand: false
        };
    }
}
